import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DatesProvider } from "../../dates/DatesContext";
import BreadcrumbDates from "../../components/core/blocks/BreadcrumbDates";
import Loading from "../../components/core/blocks/Loading";
import { DevErrorDisplay } from "../../components/core/blocks/DevErrorDisplay";
import { useDates } from "../../dates/DatesContext";
import { useFetch } from "../../hooks/api";
import { useQuery } from "@tanstack/react-query";
import { useGetV4ProductReportTotals } from "../../modules/perf_reports/api/product/v4ProductReportTotals";
import { Container, Row, Col } from "react-bootstrap";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "../../utils/dashboardLinks";
import { useAppSelector } from "../../redux/hooks";
import CouponReport from "../../modules/merchandising/components/CouponReport";
import { formatCurrency, formatPercent, formatNumber } from "../../utils/formatNumber";
import { ButtonGroup } from "../../components/core/basic/ButtonGroup";
import { Accordion } from "../../components/core/basic/Accordion";
import { useParams } from "@tanstack/react-router";
import { ProductCell } from "../../modules/merchandising/components/cells/ProductCell";
import moment from "moment";
import { CurrentVsPreviousGroupedColumnChart } from "../../modules/pricingv2/charts/CurrentVsPreenrollmentChart";
import { token } from "../../../styled-system/tokens";
import { useClientTable } from "../../components/table/tableFeatures";
import { createColumnBuilder } from "../../components/table/columnBuilder";
import { Table } from "../../components/table/Table";
import { RT8Pagination } from "../../components/table/Pagination";
import { HStack } from "../../../styled-system/jsx";
import { css } from "../../../styled-system/css";
import { useDateFormatter } from "../../i18n/useDateFormatter";

// TODO factor this out into a utils file
const DASHBOARD_TYPES = {
  PRODUCT: "product",
  CATEGORY: "category",
  ROOT: "root",
};

const getDashboardTo = ({ resourceId, type }) => {
  let to;
  switch (type) {
    case DASHBOARD_TYPES.CATEGORY:
      to = `/user/merchandising/category/${resourceId}`;
      break;
    case DASHBOARD_TYPES.PRODUCT:
      to = `/user/merchandising/product/${resourceId}`;
      break;
    default:
      to = `/user/dashboard`;
  }
  return to;
};

const Metric = ({ title, value, previous }) => {
  return (
    <Col style={{ flex: "0 0 auto", width: "16.666667%", maxWidth: "16.666667%" }} className="col-xl-2 col-lg-3 col-6">
      <div className="p-4 mb-3 card">
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <div className="text-end" style={{ marginRight: "10px" }}>
            <h6 className="fs-standard" style={{ height: "34px", fontWeight: 800 }}>
              {title}
            </h6>
            <h3 style={{ fontWeight: 600, fontSize: "2rem", marginBottom: "0.2rem", marginTop: "0.2rem" }}>{value}</h3>

            <div style={{ minHeight: "20px" }}>
              <small>
                {previous && <span style={{ color: "rgb(44, 44, 44)", fontWeight: "bold" }}>{previous}</span>}
                {!previous && <span style={{ color: "rgb(44, 44, 44)", fontWeight: "bold" }}>&nbsp;</span>}
              </small>
            </div>
          </div>
          <div
            style={{
              transition: "0.3s ease-in-out",
              marginLeft: "1rem",
              backgroundColor: "rgb(98, 63, 127)",
              borderColor: "rgb(98, 63, 127)",
              borderWidth: "1px",
              width: "6px",
              borderRadius: "4px",
              display: "block",
              boxShadow: "rgb(98, 63, 127) 0px 0px 14px",
            }}
          ></div>
        </div>
      </div>
    </Col>
  );
};

const incrementMetrics = (couponMetrics, coupon) => {
  couponMetrics.redemptions += coupon.metrics.redemptions ?? 0;
  couponMetrics.clips += coupon.metrics.clips ?? 0;
  couponMetrics.budget_spent += coupon.metrics.budget_spent ?? 0;
  couponMetrics.sales += coupon.metrics.sales ?? 0;
  couponMetrics.total_discount += coupon.metrics.total_discount ?? 0;
  couponMetrics.budget += coupon.metrics.budget ?? 0;

  couponMetrics.redemption_rate = couponMetrics.clips > 0 ? couponMetrics.redemptions / couponMetrics.clips : 0;
  couponMetrics.sales_per_redemption =
    couponMetrics.redemptions > 0 ? couponMetrics.sales / couponMetrics.redemptions : 0;
  couponMetrics.discount_percent_of_sales =
    couponMetrics.sales > 0 ? couponMetrics.budget_spent / couponMetrics.sales : 0;
  couponMetrics.cost_per_redemption =
    couponMetrics.redemptions > 0 ? couponMetrics.budget_spent / couponMetrics.redemptions : 0;
  couponMetrics.budget_percentage_used =
    couponMetrics.budget > 0 ? couponMetrics.budget_spent / couponMetrics.budget : 0;

  return couponMetrics;
};

const defaultMetrics = {
  clips: 0,
  redemptions: 0,
  budget_spent: 0,
  budget: 0,
  sales: 0,
  redemption_rate: 0,
  total_discount: 0,
  sales_per_redemption: 0,
  discount_percent_of_sales: 0,
  cost_per_redemption: 0,
  budget_percentage_used: 0,
};

const insightColumnHelper = createColumnBuilder();

const InsightList = ({ insights, discountType, marketPlace }) => {
  const { t } = useTranslation(["merchandising"]);
  const insightData = useMemo(() => {
    const insightValues = Object.values(insights);
    const insightKeys = Object.keys(insights);
    return insightValues.map((value, index) => ({
      name: insightKeys[index],
      ...value,
    }));
  }, [insights]);

  const columns = useMemo(
    () => [
      insightColumnHelper.accessor("name", {
        header: t(($) => $.merchandising["Discount Type"]),
        enableHiding: false,
        size: 150,
        cell: ({ getValue }) => (
          <div>
            {discountType === "AMOUNT_OFF_LIST_PRICE" && (
              <div className="fw-bold">{formatCurrency(parseInt(getValue()), { marketPlace })}</div>
            )}
            {discountType === "PERCENT_OFF_LIST_PRICE" && (
              <div className="fw-bold">{formatPercent(parseFloat(getValue()) / 100, { decimalPlaces: 0 })}</div>
            )}
          </div>
        ),
      }),
      insightColumnHelper.accessor("budget", {
        header: t(($) => $.merchandising["Budget"]),
        cell: ({ getValue }) => formatCurrency(parseFloat(getValue()), { marketPlace }),
        meta: { justify: "end" },
      }),
      insightColumnHelper.accessor("budget_spent", {
        header: t(($) => $.merchandising["Budget Spent"]),
        cell: ({ getValue }) => formatCurrency(parseFloat(getValue()), { marketPlace }),
        meta: { justify: "end" },
      }),
      insightColumnHelper.accessor("sales", {
        header: t(($) => $.merchandising["Sales"]),
        cell: ({ getValue }) => formatCurrency(parseFloat(getValue()), { marketPlace }),
        meta: { justify: "end" },
      }),
      insightColumnHelper.accessor("clips", {
        header: t(($) => $.merchandising["Clips"]),
        meta: { justify: "end" },
      }),
      insightColumnHelper.accessor("redemptions", {
        header: t(($) => $.merchandising["Redemptions"]),
        meta: { justify: "end" },
      }),
      insightColumnHelper.accessor("redemption_rate", {
        header: t(($) => $.merchandising["Redemption Rate"]),
        cell: ({ getValue }) => formatPercent(parseFloat(getValue()), { decimalPlaces: 1 }),
        meta: { justify: "end" },
      }),
      insightColumnHelper.accessor("sales_per_redemption", {
        header: t(($) => $.merchandising["Sales per Redemption"]),
        cell: ({ getValue }) => formatCurrency(parseFloat(getValue()), { marketPlace }),
        meta: { justify: "end" },
      }),
      insightColumnHelper.accessor("discount_percent_of_sales", {
        header: t(($) => $.merchandising["ACOS"]),
        cell: ({ getValue }) => formatPercent(parseFloat(getValue())),
        meta: { justify: "end" },
      }),
      insightColumnHelper.accessor("cost_per_redemption", {
        id: "budget_efficiency",
        header: t(($) => $.merchandising["Cost per Redemption"]),
        cell: ({ getValue }) => formatCurrency(parseFloat(getValue()), { marketPlace }),
        meta: { justify: "end" },
      }),
    ],
    [discountType, marketPlace, t],
  );

  const table = useClientTable({
    data: insightData,
    columns,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 20 },
    },
  });

  return (
    <>
      <HStack padding={"small"}>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
    </>
  );
};

const productColumnHelper = createColumnBuilder();

const CouponDetails = ({ couponId }) => {
  const { t } = useTranslation(["merchandising"]);
  const dateFmt = useDateFormatter("medium");
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel).currentChannel;

  // Pulls dates from provided context
  const { start, end, preStart, preEnd, group } = useDates();

  // Data fetching
  const filters = {
    market_place: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    channel: channel ?? "amazon",
    report_id: couponId,
  };

  const { data: couponStats, isLoading: isCouponStatsLoading } = useFetch(
    ["couponDetails", marketPlace, group, start, end, preStart, preEnd, couponId],
    "/merchandise/coupons_asin_metrics",
    filters,
    {
      select: (data) => data?.data,
    },
  );

  const metrics = useMemo(() => {
    if (!couponStats) return {};

    const couponMetrics = couponStats.results[0];

    return couponMetrics;
  }, [couponStats]);

  const productData = useMemo(() => {
    if (!couponStats || !couponStats?.results || couponStats?.results.length === 0) return [];

    const stats = couponStats.results[0];
    const productDetails = stats.product_details;
    const productMetrics = stats.product_ids;

    const productData = {};
    for (const product of productMetrics) {
      productData[product.product_id] = product;
    }
    for (const product of productDetails) {
      if (product.id in productData) {
        productData[product.id] = {
          ...productData[product.id],
          ...product,
        };
      }
    }

    const data = Object.values(productData);
    // order by total_units_ordered desc
    data.sort((a, b) => b.total_quantity_ordered - a.total_quantity_ordered);

    return data;
  }, [couponStats]);

  const productReportParams = useMemo(() => {
    if (!metrics) return null;

    // find all unique asins from metrics.product_details
    const asins = [...new Set(metrics?.product_details?.map((product) => product.asin))];
    const startDate = moment(metrics?.coupon_report?.start_time);
    let endDate = moment(metrics?.coupon_report?.end_time);

    if (endDate > moment()) {
      endDate = moment();
    }
    const diffDays = moment(endDate).diff(moment(startDate), "days");
    const couponPreStart = moment(startDate).subtract(diffDays, "days");
    const couponPreEnd = moment(startDate).subtract(1, "days");

    return {
      filters: [{ id: "asin", value: { op: "anyof", value: asins } }],
      start: startDate.toDate(),
      end: endDate.toDate(),
      preStart: couponPreStart.toDate(),
      preEnd: couponPreEnd.toDate(),
    };
  }, [metrics]);

  const totalsOptionsGetter = useGetV4ProductReportTotals();
  const { data: productStatsResponse, isLoading: isProductStatsLoading } = useQuery({
    ...totalsOptionsGetter(productReportParams),
    enabled: !!productReportParams,
  });
  const productStats = productStatsResponse?.data;

  const breadCrumbItems = [
    AGENCY_DASHBOARD_BREADCRUMB_SPEC,
    {
      name: t(($) => $.merchandising["Merchandising Dashboard"]),
      href: getDashboardTo({
        resourceId: null,
        type: DASHBOARD_TYPES.ROOT,
      }),

      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      unsorted: true,
      dropdownSubtitle: t(($) => $.merchandising["Dashboards"]),
    },
    {
      name: t(($) => $.merchandising["Coupon Report"]),
      href: "/user/merchandising/coupons",
    },
    {
      name: metrics?.coupon_report?.name,
      href: `/user/merchandising/coupons/${couponId}`,
    },
  ];

  const productColumns = useMemo(
    () => [
      productColumnHelper.accessor("product_title", {
        id: "Product",
        header: t(($) => $.merchandising["Product"]),
        enableHiding: false,
        size: 500,
        cell: (props) => (
          <ProductCell
            original={{
              ...props.row.original,
              id: props.row.original.product_id,
              cellContext: "couponProuctDetails",
            }}
            value={props.getValue()}
          />
        ),
      }),
      productColumnHelper.accessor("total_difference", {
        id: "Sales",
        header: t(($) => $.merchandising["Promo Sales"]),
        cell: ({ getValue }) => formatCurrency(parseFloat(getValue()), { marketPlace }),
        meta: { justify: "end" },
      }),
      productColumnHelper.accessor("total_promotion_discount", {
        id: "Discounts",
        header: t(($) => $.merchandising["Discounts"]),
        cell: ({ getValue }) => formatCurrency(parseFloat(getValue()), { marketPlace }),
        meta: { justify: "end" },
      }),
      productColumnHelper.accessor("total_quantity_ordered", {
        id: "Units",
        header: t(($) => $.merchandising["Units"]),
        cell: ({ getValue }) => parseInt(getValue()),
        meta: { justify: "end" },
      }),
      productColumnHelper.display({
        id: "ACOS",
        header: t(($) => $.merchandising["ACOS"]),
        cell: ({ row }) =>
          formatPercent(
            parseFloat(row.original.total_promotion_discount) / parseFloat(row.original.total_order_item_value),
          ),
        meta: { justify: "end" },
      }),
    ],
    [marketPlace, t],
  );

  const productTable = useClientTable({
    data: productData,
    columns: productColumns,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 20 },
      sorting: [{ id: "Units", desc: true }],
    },
  });

  return (
    <Container
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      <Row>
        <Col className="mb-4" xs={12}>
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>
      {(isCouponStatsLoading || isProductStatsLoading) && (
        <Row>
          <Col className="mb-4 px-3" xs={12}>
            <Loading fullPage={false} height={"300px"} />
          </Col>
        </Row>
      )}
      {!isCouponStatsLoading && !isProductStatsLoading && (
        <>
          <Row
            style={{
              display: "flex",
              flexWrap: "nowrap",
              overflow: "auto",
              paddingTop: "1rem",
              paddingBottom: "0.25rem",
              marginBottom: "0.5rem",
            }}
          >
            <Metric
              title={t(($) => $.merchandising["Redemptions / Clips"])}
              value={
                <>
                  {metrics?.redemptions} / {metrics?.clips}
                </>
              }
              previous={
                <div>
                  {t(($) => $.merchandising["Redemption Rate: {rate}"], {
                    rate: formatPercent(metrics?.redemption_rate, {
                      decimalPlaces: 1,
                    }),
                  })}
                </div>
              }
            />
            <Metric
              title={t(($) => $.merchandising["Budget Spent"])}
              value={formatCurrency(metrics?.budget_spent, {
                marketPlace: marketPlace,
              })}
              previous={
                <div>
                  {t(($) => $.merchandising["Budget: {amount}"], {
                    amount: formatCurrency(metrics?.budget, {
                      marketPlace: marketPlace,
                    }),
                  })}
                </div>
              }
            />
            <Metric
              title={t(($) => $.merchandising["Sales"])}
              value={formatCurrency(metrics?.sales, {
                marketPlace: marketPlace,
              })}
              previous={t(($) => $.merchandising["Discounts {amount}"], {
                amount: formatCurrency(metrics?.total_discount, {
                  marketPlace: marketPlace,
                }),
              })}
            />
            <Metric
              title={t(($) => $.merchandising["ACOS"])}
              value={formatPercent(metrics?.discount_percent_of_sales, {
                decimalPlaces: 1,
              })}
            />
            <Metric
              title={t(($) => $.merchandising["Sales per Redemption"])}
              value={formatCurrency(metrics?.sales_per_redemption, {
                marketPlace: marketPlace,
              })}
            />
            <Metric
              title={t(($) => $.merchandising["Cost per Redemption"])}
              value={formatCurrency(metrics?.cost_per_redemption, {
                marketPlace: marketPlace,
              })}
            />
          </Row>

          <Row>
            <Col className="px-5" xs={12}>
              <h5>
                {metrics.coupon_report.name}
                <div>
                  <small>
                    {metrics.coupon_report.website_message}

                    <span className="ps-2 fw-bold">
                      {dateFmt.format(moment(metrics.coupon_report.start_time).toDate())} -{" "}
                      {dateFmt.format(moment(metrics.coupon_report.end_time).toDate())}
                    </span>
                  </small>
                </div>
              </h5>
            </Col>
          </Row>

          <Row>
            <Col className="mb-4 px-5" xs={12}>
              <Accordion title={t(($) => $.merchandising["Insights"])} defaultExpanded={true}>
                <Row>
                  <Col className="fs-5">
                    <div>
                      <div className="fs-3 text-center pb-1">
                        <span className="fw-bold pe-3">&nbsp; {t(($) => $.merchandising["CR"])}</span>
                        {formatPercent(productStats.conv_rate - productStats.conv_rate_prev, {
                          addPlus: true,
                        })}
                      </div>
                      <CurrentVsPreviousGroupedColumnChart
                        data={[
                          {
                            title: t(($) => $.merchandising["CR"]),
                            current: productStats.conv_rate ? productStats.conv_rate : 0,
                            previous: productStats.conv_rate_prev ? productStats.conv_rate_prev : 0,
                            color: token("colors.trellisPalette.3"),
                          },
                        ].filter(Boolean)}
                        isPercentage
                        currentLabel={t(($) => $.merchandising["Coupon"])}
                      />
                    </div>
                  </Col>
                  <Col className="fs-5">
                    <div>
                      <div className="fs-3 text-center pb-1">
                        <span className="fw-bold pe-3">&nbsp; {t(($) => $.merchandising["GCD"])}</span>
                        {formatCurrency(productStats.profit - productStats.profit_prev, {
                          marketPlace: marketPlace,
                          addPlus: true,
                        })}
                      </div>
                      <CurrentVsPreviousGroupedColumnChart
                        data={[
                          {
                            title: t(($) => $.merchandising["GCD"]),
                            current: productStats.profit ? productStats.profit : 0,
                            previous: productStats.profit_prev ? productStats.profit_prev : 0,
                            color: token("colors.trellisPalette.3"),
                          },
                        ].filter(Boolean)}
                        isCurrency
                        currentLabel={t(($) => $.merchandising["Coupon"])}
                      />
                    </div>
                  </Col>
                  <Col className="fs-5">
                    <div>
                      <div className="fs-3 text-center pb-1">
                        <span className="fw-bold pe-3">&nbsp; {t(($) => $.merchandising["Sales"])}</span>
                        {formatCurrency(productStats.total_sales - productStats.total_sales_prev, {
                          marketPlace: marketPlace,
                          addPlus: true,
                        })}
                      </div>
                      <CurrentVsPreviousGroupedColumnChart
                        data={[
                          {
                            title: t(($) => $.merchandising["Sales"]),
                            current: productStats.total_sales ? productStats.total_sales : 0,
                            previous: productStats.total_sales_prev ? productStats.total_sales_prev : 0,
                            color: token("colors.trellisPalette.3"),
                          },
                        ].filter(Boolean)}
                        isCurrency
                        currentLabel={t(($) => $.merchandising["Coupon"])}
                      />
                    </div>
                  </Col>
                  <Col className="fs-5">
                    <div>
                      <div className="fs-3 text-center pb-1">
                        <span className="fw-bold pe-3">&nbsp; {t(($) => $.merchandising["Volume"])}</span>
                        {formatNumber(productStats.total_units - productStats.total_units_prev, {
                          addPlus: true,
                        })}
                      </div>
                      <CurrentVsPreviousGroupedColumnChart
                        data={[
                          {
                            title: t(($) => $.merchandising["Sales"]),
                            current: productStats.total_units ? productStats.total_units : 0,
                            previous: productStats.total_units_prev ? productStats.total_units_prev : 0,
                            color: token("colors.trellisPalette.3"),
                          },
                        ].filter(Boolean)}
                        precision={0}
                        currentLabel={t(($) => $.merchandising["Coupon"])}
                      />
                    </div>
                  </Col>
                </Row>
              </Accordion>
            </Col>
          </Row>

          <Row>
            <Col className="mb-4 px-5" xs={12}>
              <Accordion title={t(($) => $.merchandising["Products"])} defaultExpanded={false}>
                <div
                  style={{
                    padding: "2rem",
                    border: "1px solid rgb(238, 238, 238)",
                    borderRadius: "6px",
                    backgroundColor: "rgb(248, 248, 248)",
                    marginTop: "10px",
                  }}
                  className="fs-5"
                >
                  <div className="pb-2 fw-bold">{t(($) => $.merchandising["ACOS: Coupon Cost of Sales"])}</div>
                  <div>
                    {t(
                      ($) =>
                        $.merchandising[
                          "The ACOS might differ slightly from coupon value. ACOS is determined by dividing the total promotion discount by the total value of ordered items. Occasionally, buyers purchase multiple items, but the promotion discount applies to just one item in the order, which can affect the ACOS calculation."
                        ],
                    )}
                  </div>
                </div>
                <HStack padding={"small"}>
                  <RT8Pagination className={css({ marginLeft: "auto" })} table={productTable} />
                </HStack>
                <Table table={productTable} />
              </Accordion>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

const CouponDashboard = () => {
  const { t } = useTranslation(["merchandising"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel).currentChannel;
  const [discountType, setDiscountType] = useState("all");

  // Pulls dates from provided context
  const { start, end, preStart, preEnd, group } = useDates();

  // Data fetching
  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    //   group_by: "product_id",
    channel: channel ?? "amazon",
  };

  const {
    data: couponStats,
    isLoading: isCouponStatsLoading,
    isError: isCouponStatsError,
    error: couponStatsError,
  } = useFetch(
    ["productDealStats", marketPlace, group, start, end, preStart, preEnd],
    "/merchandise/coupons_reports/",
    filters,
    {
      select: (data) => data?.data,
    },
  );

  const filteredMetrics = useMemo(() => {
    const results = couponStats?.results ?? [];
    if (discountType === "all") return { ...couponStats, results };

    return {
      ...couponStats,
      results: results.filter((coupon) => coupon.discount_type === discountType),
    };
  }, [couponStats, discountType]);

  const metrics = useMemo(() => {
    if (!filteredMetrics) return {};

    let couponMetrics = { ...defaultMetrics };

    for (const coupon of filteredMetrics.results) {
      couponMetrics = incrementMetrics(couponMetrics, coupon);
    }

    return couponMetrics;
  }, [filteredMetrics]);

  const { couponTypeCount, discountAmountCount } = useMemo(() => {
    const couponTypeCount = {
      all: 0,
      AMOUNT_OFF_LIST_PRICE: 0,
      PERCENT_OFF_LIST_PRICE: 0,
    };

    const discountAmountCount = {
      AMOUNT_OFF_LIST_PRICE: {},
      PERCENT_OFF_LIST_PRICE: {},
    };

    for (const coupon of couponStats?.results ?? []) {
      couponTypeCount.all += 1;

      // Amazon can report discount types beyond the two we bucket by; count them
      // in the "all" total but leave them out of the per-type breakdowns.
      const amountsForType = discountAmountCount[coupon.discount_type];
      if (!amountsForType) continue;

      couponTypeCount[coupon.discount_type] += 1;

      if (!(coupon.discount_amount in amountsForType)) {
        amountsForType[coupon.discount_amount] = { ...defaultMetrics };
      }

      amountsForType[coupon.discount_amount] = incrementMetrics(amountsForType[coupon.discount_amount], coupon);
    }

    return { couponTypeCount, discountAmountCount };
  }, [couponStats]);

  const buttunBuckets = [
    { label: t(($) => $.merchandising["All Coupons"]), value: "all", counter: couponTypeCount?.all },
    {
      label: t(($) => $.merchandising["Amount of List Price"]),
      value: "AMOUNT_OFF_LIST_PRICE",
      counter: couponTypeCount?.AMOUNT_OFF_LIST_PRICE,
    },
    {
      label: t(($) => $.merchandising["Percent of List Price"]),
      value: "PERCENT_OFF_LIST_PRICE",
      counter: couponTypeCount?.PERCENT_OFF_LIST_PRICE,
    },
  ];

  const breadCrumbItems = [
    AGENCY_DASHBOARD_BREADCRUMB_SPEC,
    {
      name: t(($) => $.merchandising["Merchandising Dashboard"]),
      href: getDashboardTo({
        resourceId: null,
        type: DASHBOARD_TYPES.ROOT,
      }),

      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      unsorted: true,
      dropdownSubtitle: t(($) => $.merchandising["Dashboards"]),
    },
    {
      name: t(($) => $.merchandising["Coupon Report"]),
      href: "/user/merchandising/coupons",
    },
  ];

  return (
    <Container
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      <Row>
        <Col className="mb-4" xs={12}>
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>
      {isCouponStatsLoading && (
        <Row>
          <Col className="mb-4 px-3" xs={12}>
            <Loading fullPage={false} height={"300px"} />
          </Col>
        </Row>
      )}
      {isCouponStatsError && (
        <DevErrorDisplay
          error={couponStatsError}
          userMessage={t(($) => $.merchandising["Error loading coupon report."])}
        />
      )}
      {!isCouponStatsLoading && !isCouponStatsError && (
        <Row>
          <Col className="mb-4 px-3" xs={12}>
            <Row className="p-3">
              <Col>
                <ButtonGroup
                  buttons={buttunBuckets}
                  value={discountType}
                  onSelect={(values) => {
                    setDiscountType(values);
                  }}
                />
              </Col>
            </Row>

            {metrics && (
              <Row
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  overflow: "auto",
                  paddingTop: "1rem",
                  paddingBottom: "0.25rem",
                  marginBottom: "0.5rem",
                }}
              >
                <Metric
                  title={t(($) => $.merchandising["Redemptions / Clips"])}
                  value={
                    <div>
                      {metrics?.redemptions} / {metrics?.clips}
                    </div>
                  }
                  previous={
                    <div>
                      {t(($) => $.merchandising["Redemption Rate: {rate}"], {
                        rate: formatPercent(metrics?.redemption_rate, {
                          decimalPlaces: 1,
                        }),
                      })}
                    </div>
                  }
                />
                <Metric
                  title={t(($) => $.merchandising["Budget Spent"])}
                  value={formatCurrency(metrics?.budget_spent, {
                    marketPlace: marketPlace,
                  })}
                  previous={
                    <div>
                      {t(($) => $.merchandising["Budget: {amount}"], {
                        amount: formatCurrency(metrics?.budget, {
                          marketPlace: marketPlace,
                        }),
                      })}
                    </div>
                  }
                />
                <Metric
                  title={t(($) => $.merchandising["Sales"])}
                  value={formatCurrency(metrics?.sales, {
                    marketPlace: marketPlace,
                  })}
                  previous={t(($) => $.merchandising["Discounts {amount}"], {
                    amount: formatCurrency(metrics?.total_discount, {
                      marketPlace: marketPlace,
                    }),
                  })}
                />
                <Metric
                  title={t(($) => $.merchandising["ACOS"])}
                  value={formatPercent(metrics?.discount_percent_of_sales, {
                    decimalPlaces: 1,
                  })}
                />
                <Metric
                  title={t(($) => $.merchandising["Sales per Redemption"])}
                  value={formatCurrency(metrics?.sales_per_redemption, {
                    marketPlace: marketPlace,
                  })}
                />
                <Metric
                  title={t(($) => $.merchandising["Cost per Redemption"])}
                  value={formatCurrency(metrics?.cost_per_redemption, {
                    marketPlace: marketPlace,
                  })}
                />
              </Row>
            )}

            {discountType !== "all" && (
              <Accordion title={t(($) => $.merchandising["Insights"])} defaultExpanded={true}>
                <InsightList
                  insights={discountAmountCount[discountType]}
                  discountType={discountType}
                  marketPlace={marketPlace}
                />
              </Accordion>
            )}

            <Accordion title={t(($) => $.merchandising["Coupon List"])} defaultExpanded={true}>
              <CouponReport data={filteredMetrics} marketPlace={marketPlace} />
            </Accordion>
          </Col>
        </Row>
      )}
    </Container>
  );
};

const MerchCoupons = () => {
  const { coupon: couponId = null } = useParams({ strict: false });
  return (
    <DatesProvider>
      {couponId === null && <CouponDashboard />}
      {couponId !== null && <CouponDetails couponId={couponId} />}
    </DatesProvider>
  );
};

export default MerchCoupons;
